import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Factory, ClipboardList, Cog, Tag, CircleDot, AlertTriangle, Bot, ChevronDown, GitBranch } from 'lucide-react';
import { useMESStore } from '@/stores/useMESStore';
import { useAuth } from '@/contexts/AuthContext';
import { MOCK_PLANT_CATALOG } from '@/data/mockPlantCatalog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const storageKey = (uid: string | null | undefined) => `expai_active_context_${uid ?? 'anon'}`;

interface PersistedContext {
  plant: string | null;
  line: string | null;
  station: string | null;
}

/**
 * MES Context Bar — universal fixed bar rendered immediately below the
 * top header on every authenticated route. Renders cascading dropdowns
 * for Plant › Line › Station and persists the selection per user.
 */
export function ContextBar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    activePlant, activeLine, activeStation,
    activeOrderId, activeOperation, activeSerial,
    machines, alerts,
    setActivePlant, setActiveLine, setActiveStation,
  } = useMESStore();
  const unack = alerts.filter((a) => !a.acknowledged).length;
  const hydrated = useRef(false);

  // Hydrate from localStorage once user is known.
  useEffect(() => {
    if (hydrated.current) return;
    try {
      const raw = window.localStorage.getItem(storageKey(user?.id));
      if (raw) {
        const parsed = JSON.parse(raw) as PersistedContext;
        if (parsed.plant) setActivePlant(parsed.plant);
        if (parsed.line) setActiveLine(parsed.line);
        if (parsed.station) setActiveStation(parsed.station);
      }
    } catch {
      // ignore corrupt storage
    }
    hydrated.current = true;
  }, [user?.id, setActivePlant, setActiveLine, setActiveStation]);

  // Persist on change (only after hydration).
  useEffect(() => {
    if (!hydrated.current) return;
    try {
      const payload: PersistedContext = { plant: activePlant, line: activeLine, station: activeStation };
      window.localStorage.setItem(storageKey(user?.id), JSON.stringify(payload));
    } catch {
      // ignore
    }
  }, [activePlant, activeLine, activeStation, user?.id]);

  const plant = MOCK_PLANT_CATALOG.find((p) => p.id === activePlant) ?? null;
  const line = plant?.lines.find((l) => l.id === activeLine) ?? null;
  const station = line?.stations.find((s) => s.id === activeStation) ?? null;

  const machineStatus = machines.find((m) => m.line === 'Line A')?.status ?? 'IDLE';
  const statusColor: Record<string, string> = {
    RUNNING: 'text-[hsl(var(--success))]',
    PAUSED: 'text-[hsl(var(--warning))]',
    ERROR: 'text-destructive',
    IDLE: 'text-muted-foreground',
    MAINTENANCE: 'text-[hsl(var(--warning))]',
  };

  return (
    <div className="flex h-9 items-center gap-1 overflow-x-auto border-b bg-card px-4 text-xs shrink-0">
      {/* Plant selector */}
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1.5 whitespace-nowrap rounded px-1.5 py-0.5 hover:bg-muted transition-colors">
          <Factory className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">{t('contextBar.plant', 'Planta')}:</span>
          <span className="font-medium text-foreground">{plant?.name ?? '—'}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[180px]">
          {MOCK_PLANT_CATALOG.map((p) => (
            <DropdownMenuItem key={p.id} onSelect={() => setActivePlant(p.id)}>
              {p.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Divider />

      {/* Line selector */}
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={!plant}
          className="flex items-center gap-1.5 whitespace-nowrap rounded px-1.5 py-0.5 hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <GitBranch className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">{t('contextBar.line', 'Línea')}:</span>
          <span className="font-medium text-foreground">{line?.name ?? '—'}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[180px]">
          {(plant?.lines ?? []).map((l) => (
            <DropdownMenuItem key={l.id} onSelect={() => setActiveLine(l.id)}>
              {l.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Divider />

      {/* Station selector */}
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={!line}
          className="flex items-center gap-1.5 whitespace-nowrap rounded px-1.5 py-0.5 hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Factory className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">{t('contextBar.station', 'Estación')}:</span>
          <span className="font-medium text-foreground">{station?.name ?? '—'}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[180px]">
          {(line?.stations ?? []).map((s) => (
            <DropdownMenuItem key={s.id} onSelect={() => setActiveStation(s.id)}>
              {s.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Divider />

      <ContextItem icon={ClipboardList} label={t('contextBar.order')} value={activeOrderId} />
      <Divider />
      <ContextItem icon={Cog} label={t('contextBar.operation')} value={activeOperation} />
      <Divider />
      <ContextItem icon={Tag} label={t('contextBar.serial')} value={activeSerial} />
      <Divider />
      <div className="flex items-center gap-1.5">
        <CircleDot className={`h-3 w-3 ${statusColor[machineStatus]}`} />
        <span className={`font-semibold ${statusColor[machineStatus]}`}>{machineStatus}</span>
      </div>
      <Divider />
      <div className="flex items-center gap-1.5">
        <AlertTriangle className={`h-3 w-3 ${unack > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
        <span className={unack > 0 ? 'font-semibold text-destructive' : 'text-muted-foreground'}>
          {t('contextBar.alerts')}: {unack}
        </span>
      </div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="ml-auto flex items-center">
              <Bot className="h-3.5 w-3.5 text-primary" />
            </div>
          </TooltipTrigger>
          <TooltipContent>Agente activo</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function ContextItem({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      <Icon className="h-3 w-3 text-muted-foreground" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

function Divider() {
  return <span className="mx-2 text-border">|</span>;
}
